import { formatDate } from '@angular/common';
import { Component, ChangeDetectionStrategy } from '@angular/core';
import { EChartOption, graphic } from 'echarts/lib/echarts';

import { environment } from 'src/environments/environment';
import { ValueCell } from '../../table/models/input/value-cell';
import { MarklineChartDataModel } from '../models/MarkLineChartDataModel';
import { GlobalChartDataModel } from './GlobalChartDataModel';
import { BaseChartServiceInterface } from './interfaces/BaseChartServiceInterface';

@Component({
    template: '',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export abstract class BaseChartComponent implements BaseChartServiceInterface {
  options: EChartOption;

  constructor() {
    this.options = {};
  }

  getOptions(): any {
    return this.options;
  }

  // elements
  initXAxis(
    name: string,
    position: 'top' | 'bottom',
    inverse: boolean,
    max: number,
    min: number,
    splitineShow: boolean,
    axisTickShow: boolean,
    splitlineWidth: number,
    splitlineColor: string,
    splitlineType: 'solid' | 'dashed' | 'dotted'
  ): void {
    const axis: EChartOption.XAxis = {};
    if (name !== null) {
      axis.name = name;
    }
    if (position !== null) {
      axis.position = position;
    }
    if (inverse !== null) {
      axis.inverse = inverse;
    }
    if (max !== null) {
      axis.max = max;
    }
    if (min !== null) {
      axis.min = min;
    }
    this.initAxisTick(axis, axisTickShow);
    this.initSplitline(
      axis,
      splitineShow,
      splitlineWidth,
      splitlineColor,
      splitlineType
    );

    this.options.xAxis = axis;
  }

  initYAxis(
    name: string,
    position: 'left' | 'right',
    inverse: boolean,
    max: number,
    min: number,
    axisLabelShow: boolean,
    axisTickShow: boolean,
    splitineShow: boolean,
    splitlineWidth: number,
    splitlineColor: string,
    splitlineType: 'solid' | 'dashed' | 'dotted'
  ): void {
    const axis: EChartOption.YAxis = {};
    if (name !== null) {
      axis.name = name;
    }
    if (position !== null) {
      axis.position = position;
    }
    if (inverse !== null) {
      axis.inverse = inverse;
    }
    if (max !== null) {
      axis.max = max;
    }
    if (min !== null) {
      axis.min = min;
    }
    // axisLabel
    axis.axisLabel = {};
    if (axisLabelShow !== null) {
      axis.axisLabel.show = axisLabelShow;
    }
    this.initAxisTick(axis, axisTickShow);
    this.initSplitline(
      axis,
      splitineShow,
      splitlineWidth,
      splitlineColor,
      splitlineType
    );
    this.options.yAxis = axis;
  }

  initSplitline(
    axis: EChartOption.XAxis | EChartOption.YAxis,
    splitineShow: boolean,
    splitlineWidth: number,
    splitlineColor: string,
    splitlineType: 'solid' | 'dashed' | 'dotted'
  ): void {
    axis.splitLine = {};
    axis.splitLine.lineStyle = {};
    if (splitineShow !== null) {
      axis.splitLine.show = splitineShow;
    }
    if (splitlineWidth !== null) {
      axis.splitLine.lineStyle.width = splitlineWidth;
    }
    if (splitlineColor !== null) {
      axis.splitLine.lineStyle.color = splitlineColor;
    }
    if (splitlineType !== null) {
      axis.splitLine.lineStyle.type = splitlineType;
    }
  }

  initAxisTick(
    axis: EChartOption.XAxis | EChartOption.YAxis,
    axisTickShow: boolean
  ): void {
    axis.axisTick = {};
    if (axisTickShow !== null) {
      axis.axisTick.show = axisTickShow;
    }
  }

  addSerie(serieMarkLine: EChartOption.Series): void {
    this.options.series.push(serieMarkLine);
  }

  createRenderItemRect(): any {
    return function renderItem(params, api): any {
      const xMin = api.value(0);
      const xLenght = xMin - api.value(2);
      const yMin = api.value(1);
      const yLenght = yMin - api.value(3);

      const start = api.coord([xMin, yMin]);
      const end = api.coord([xLenght, yLenght]);

      const rectShape = graphic.clipRectByRect(
        {
          x: start[0],
          y: start[1],
          width: end[0] - start[0],
          height: end[1] - start[1],
        },
        {
          x: params.coordSys.x,
          y: params.coordSys.y,
          width: params.coordSys.width,
          height: params.coordSys.height,
        }
      );

      return (
        rectShape && {
          type: 'rect',
          shape: rectShape,
          style: api.style(),
        }
      );
    };
  }

  /**
   * Use this method to generate ONLY expected values for 'data' attribute
   * Echarts format => data : [{value: [x, y, width, height]}]
   * @param chartDataList GlobalChartDataModel[]
   */
  convertRectDataChartListToExpectedValues(
    chartDataList: GlobalChartDataModel[]
  ): any {
    const expectedValues = [];
    if (!chartDataList) {
      return [];
    }
    chartDataList.forEach((rectChartData) => {
      const data: any = {};
      data.value = this.convertRectDataChartToExpectedValues(rectChartData);
      data.tooltip = rectChartData.tooltipFormatter;
      expectedValues.push(data);
    });
    return expectedValues;
  }

  /**
   * Use this method to generate expected values for 'data' attribute
   * Echarts format => data : [[x, y, width, height]]
   * @param chartData GlobalChartDataModel
   */
  convertRectDataChartToExpectedValues(
    chartData: GlobalChartDataModel
  ): number[] {
    if (!chartData) {
      return [];
    }

    return [
      chartData.value.x,
      chartData.value.y,
      chartData.value.width,
      chartData.value.height,
    ];
  }

  /**
   * Convert MarklineChartData[] to expected values by chart library
   * @param marklineChartDataList MarklineChartData swagger object
   */
  convertMarklineListToExpectedValues(
    marklineChartDataList: MarklineChartDataModel[]
  ): any {
    const expectedValues = [];
    if (!marklineChartDataList) {
      return [];
    }
    marklineChartDataList.forEach((marklineChartData) => {
      const data = this.convertMarklineToExpectedValues(marklineChartData);
      expectedValues.push(data);
    });
    return expectedValues;
  }

  /**
   * Convert MarklineChartData[] to expected values by chart library
   * @param marklineChartDataList MarklineChartData swagger object
   */
  convertMarklineToExpectedValues(
    marklineChartData: MarklineChartDataModel
  ): any {
    const data: any = {
      yAxis: marklineChartData.y,
    };
    return data;
  }

  slider(
    Iorient: EChartOption.DataZoom.Slider['orient'],
    IfilterMode: EChartOption.DataZoom.Slider['filterMode'] = 'filter'
  ): EChartOption.DataZoom.Slider {
    const newSlider: any = {
      type: 'slider',
      filterMode: IfilterMode,
      orient: Iorient,
      backgroundColor: environment.core.CHART_ZOOM_BACKGROUND_COLOR.toString(),
      borderColor: environment.core.CHART_ZOOM_BORDER_COLOR.toString(),
      handleSize: environment.core.CHART_ZOOM_HANDLE_SIZE,
      handleStyle: {
        shadowBlur: environment.core.CHART_ZOOM_HANDLE_STYLE_SHADOW_BLUR,
        shadowOffsetX: environment.core.CHART_ZOOM_HANDLE_STYLE_SHADOW_X,
        shadowOffsetY: environment.core.CHART_ZOOM_HANDLE_STYLE_SHADOW_Y,
        shadowColor: environment.core.CHART_ZOOM_HANDLE_STYLE_SHADOW_COLOR,
        opacity: environment.core.CHART_ZOOM_HANDLE_STYLE_OPACITY,
      },
    };
    if (Iorient === 'vertical') {
      newSlider.width = environment.core.CHART_ZOOM_HANDLE_THICKNESS;
    } else {
      newSlider.height = environment.core.CHART_ZOOM_HANDLE_THICKNESS;
      newSlider.bottom = 0;
    }
    return newSlider;
  }

  title(Itext: string): void {
    const title: EChartOption['title'] = {
      text: Itext,
      backgroundColor: environment.core.CHART_TITLE_BACKGROUND_COLOR,
      borderColor: environment.core.CHART_TITLE_BORDER_COLOR,
      borderWidth: environment.core.CHART_TITLE_BORDER_WIDTH,
      borderRadius: environment.core.CHART_TITLE_BORDER_RADIUS,
      left: environment.core.CHART_TITLE_HORIZONTAL_ALIGN,
      padding: environment.core.CHART_TITLE_PADDING,
      textStyle: {
        fontFamily: environment.core.CHART_TITLE_FONT_FAMILY,
        fontSize: environment.core.CHART_TITLE_FONT_SIZE,
        fontStyle: environment.core.CHART_TITLE_FONT_STYLE,
        fontWeight: environment.core.CHART_TITLE_FONT_WEIGHT,
      },
    };
    this.options.title = title;
  }

  // ==========================================================
  //                          SERIES
  // ==========================================================
  initSerieMarkLine(
    data: MarklineChartDataModel[],
    colorLine: string,
    labelPosition: 'start' | 'middle' | 'end'
  ): EChartOption.SeriesLine {
    const serie: EChartOption.SeriesLine = {
      type: 'line',
      smooth: true,
      itemStyle: {
        color: colorLine,
      },
      markLine: {
        silent: true,
        symbol: 'none',
        label: {
          position: labelPosition,
        },
        data: this.convertMarklineListToExpectedValues(data),
      },
    };
    return serie;
  }

  initSerieRect(
    data: GlobalChartDataModel | GlobalChartDataModel[],
    Isilent: EChartOption.SeriesCustom['silent'],
    Icolor: EChartOption.SeriesCustom['itemStyle']['color'],
    IborderColor: EChartOption.SeriesCustom['itemStyle']['borderColor'],
    IlabelShow: boolean,
    IlabelText: string,
    IlabelColor: string
  ): EChartOption.SeriesCustom {
    const renderItemRect = this.createRenderItemRect();

    const dataOk = Array.isArray(data)
      ? this.convertRectDataChartListToExpectedValues(data)
      : [this.convertRectDataChartToExpectedValues(data)];
    const serie: any = {
      type: 'custom',
      smooth: true,
      silent: Isilent,
      itemStyle: {
        color: Icolor,
        borderColor: IborderColor,
      },
      label: {
        show: IlabelShow,
        formatter: IlabelText,
        color: IlabelColor,
      },
      renderItem: renderItemRect,
      data: dataOk,
    };
    return serie;
  }

  initSerieLine(
    data: number[] | (string | number)[][],
    indexUnit: EChartOption.SeriesLine['yAxisIndex'],
    isLiftView: boolean,
    thresholds: any,
    unit: string,
    name?: string,
    colorLine?: string,
    widthLine?: number,
    typeLine?: string,
    displayName?: boolean,
    showPoints? : boolean
  ): EChartOption.SeriesLine {
    const serie: any = {
      type: 'line',
      smooth: true,
      name: name,
      data: data,
      connectNulls: true,
      showSymbol: environment.core.showSymbol,
      symbolSize: 5,
      step: isLiftView ? 'end' : false,
      lineStyle: {
        color: colorLine,
        width: widthLine ? widthLine : 1,
        type: typeLine
      },
      itemStyle : {
        color: colorLine,
      },
      yAxisIndex: indexUnit
    };
    if(displayName) {
      serie.endLabel = {
        show: true,
        offset: [-50,-10],
        formatter: function (params) {
          return params.seriesName;
        }
      }
    }
    if (thresholds !== null) {
      serie.markLine = {
        silent: true,
        symbol: 'circle',
        label: {
          formatter: (params: any) =>
            params.value.toLocaleString('fr-FR', {
              minimumFractionDigits: 2,
              maximumFractionDigits: 4,
            }) + ' (' + params.name + ')',
          // @ts-ignore @types/echart not updated to Echart last version (5.1.1)
          backgroundColor: 'white',
          fontWeight: 'bold',
          offset: [3, 0]
        }
      };
      serie.markArea = {
        silent: true,
        itemStyle: {
          color: 'rgba(187, 220, 253, 40%)',
        },
        label: {
          show: false,
        },
        data: {},
      };
      if (thresholds.low > 0 && thresholds.high > 0) {
        serie.markLine.data = [
          // @ts-ignore @types/echart not updated to Echart last version (5.1.1)
          { name: thresholds.highLabel, yAxis: thresholds.high }, { name: thresholds.lowLabel, yAxis: thresholds.low },
        ];
        serie.markArea.data = [
          // @ts-ignore @types/echart not updated to Echart last version (5.1.1)
          [
            {
              yAxis: thresholds.low,
            },
            {
              yAxis: thresholds.high,
            },
          ],
        ];
      } else if (thresholds.low > 0 && thresholds.high === 0) {
        // @ts-ignore @types/echart not updated to Echart last version (5.1.1)
        serie.markLine.data = [{ name: thresholds.lowLabel, yAxis: thresholds.low }];
      } else if (thresholds.high > 0 && thresholds.low === 0) {
        // @ts-ignore @types/echart not updated to Echart last version (5.1.1)
        serie.markLine.data = [{ name: thresholds.highLabel, yAxis: thresholds.high }];
      }
    }
    return serie;
  }
}
